import apiClient from "./api";
import { getApiAccessToken } from "./UserService";

export interface GeneralPriceItem {
  id: number;
  itemNumber: number;
  itemSubnumber: number;
  itemCode?: string | null;
  name: string;
  description: string;
  unitName: string;
  orderInGroup: number;
  includeInReport: boolean;
  requireAdditionalInfo: boolean;
  referenceCodes: string;
  invoicingReferenceCode: string;
  isInUse: boolean;
}

export interface GeneralPriceGroup {
  id: number;
  name: string;
  description: string;
  orderInGroup: number;
  isActive: boolean;
  includeInReport: boolean;
  objectId?: number | null;
  isInUse: boolean;
  items: GeneralPriceItem[];
}

export interface GeneralPriceListResponse {
  hideWorkItemNumber: boolean;
  useWorkItemCode: boolean;
  priceGroupsByObject: boolean;
  groups: GeneralPriceGroup[];
}

export interface GeneralPriceOrderOption {
  value: number;
  label: string;
}

export interface GeneralPriceObjectOption {
  id: number;
  name: string;
}

export interface GeneralPriceGroupOption {
  id: number;
  name: string;
  orderInGroup: number;
}

export interface GeneralPriceFormMetadata {
  groupOrderOptions: GeneralPriceOrderOption[];
  objectOptions: GeneralPriceObjectOption[];
  groupOptions: GeneralPriceGroupOption[];
  unitOptions: string[];
}

export interface UpsertGeneralPriceGroupRequest {
  name: string;
  description?: string;
  orderInGroup: number;
  includeInReport: boolean;
  isActive: boolean;
  objectId?: number | null;
  currentOrderInGroup?: number | null;
}

export interface UpsertGeneralPriceItemRequest {
  priceGroupId: number;
  name: string;
  description?: string;
  orderInGroup: number;
  itemNumber: number;
  itemSubnumber: number;
  itemCode?: string | null;
  unitName: string;
  includeInReport: boolean;
  requireAdditionalInfo: boolean;
  referenceCodes?: string;
  invoicingReferenceCode?: string;
  currentOrderInGroup?: number | null;
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await getApiAccessToken();
  return { Authorization: `Bearer ${token}` };
}

export const getGeneralPriceList = async (
  addressSystemId: number,
): Promise<GeneralPriceListResponse> => {
  const headers = await authHeader();
  const response = await apiClient.get<GeneralPriceListResponse>(
    `/api/GeneralPriceList/${addressSystemId}`,
    { headers },
  );
  return response.data;
};

export const getGeneralPriceFormMetadata = async (
  addressSystemId: number,
  groupId?: number,
): Promise<GeneralPriceFormMetadata> => {
  const headers = await authHeader();
  const response = await apiClient.get<GeneralPriceFormMetadata>(
    `/api/GeneralPriceList/${addressSystemId}/metadata`,
    { headers, params: groupId ? { groupId } : undefined },
  );
  return response.data;
};

export const createGeneralPriceGroup = async (
  addressSystemId: number,
  request: UpsertGeneralPriceGroupRequest,
): Promise<number> => {
  const headers = await authHeader();
  const response = await apiClient.post<{ id: number }>(
    `/api/GeneralPriceList/${addressSystemId}/groups`,
    request,
    { headers },
  );
  return response.data.id;
};

export const updateGeneralPriceGroup = async (
  addressSystemId: number,
  groupId: number,
  request: UpsertGeneralPriceGroupRequest,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.put(
    `/api/GeneralPriceList/${addressSystemId}/groups/${groupId}`,
    request,
    { headers },
  );
};

export const deleteGeneralPriceGroup = async (
  addressSystemId: number,
  groupId: number,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.delete(
    `/api/GeneralPriceList/${addressSystemId}/groups/${groupId}`,
    { headers },
  );
};

export const createGeneralPriceItem = async (
  addressSystemId: number,
  request: UpsertGeneralPriceItemRequest,
): Promise<number> => {
  const headers = await authHeader();
  const response = await apiClient.post<{ id: number }>(
    `/api/GeneralPriceList/${addressSystemId}/items`,
    request,
    { headers },
  );
  return response.data.id;
};

export const updateGeneralPriceItem = async (
  addressSystemId: number,
  itemId: number,
  request: UpsertGeneralPriceItemRequest,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.put(
    `/api/GeneralPriceList/${addressSystemId}/items/${itemId}`,
    request,
    { headers },
  );
};

export const deleteGeneralPriceItem = async (
  addressSystemId: number,
  itemId: number,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.delete(
    `/api/GeneralPriceList/${addressSystemId}/items/${itemId}`,
    { headers },
  );
};

export const moveGeneralPriceGroup = async (
  addressSystemId: number,
  groupId: number,
  direction: -1 | 1,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.post(
    `/api/GeneralPriceList/${addressSystemId}/groups/${groupId}/move`,
    { direction },
    { headers },
  );
};

export const moveGeneralPriceItem = async (
  addressSystemId: number,
  groupId: number,
  itemId: number,
  direction: -1 | 1,
): Promise<void> => {
  const headers = await authHeader();
  await apiClient.post(
    `/api/GeneralPriceList/${addressSystemId}/groups/${groupId}/items/${itemId}/move`,
    { direction },
    { headers },
  );
};
